// Screen: the root of every screen. Full height of the app frame, themed (sets --acc/--on/--bg for the character or
// 'pre'), flex column, clips its content. With `tab`, renders XTabs at the bottom and sets --xm-content-bottom so the
// screen's scroll area can stop above it.
//   <Screen theme={ch} tab="today" badge={todoLeft}> ...header... <div class="xm-scroll" style={{ flex: 1 }}>...</div> </Screen>
// A screen that embeds other screens (K1) wraps them in <EmbeddedScreen.Provider value>: those render a <div>, so the
// page keeps one <main>.
import { createContext, type ComponentChildren, type JSX } from 'preact';
import { useContext } from 'preact/hooks';
import { themeVars, type Theme } from '../theme';
import { XTabs, type TabId } from './XTabs';

export interface ScreenProps {
  theme: Theme;
  /** Page background (default var(--bg) of the theme). */
  bg?: string;
  tab?: TabId;
  badge?: boolean | number;
  /** Accessible name (default: none; the h1 inside names it). */
  label?: string;
  children?: ComponentChildren;
  class?: string;
  style?: JSX.CSSProperties;
  /** Test hook for screenshots: data-screen="O8" etc. */
  id?: string;
}

export const EmbeddedScreen = createContext(false);

export function Screen({ theme, bg, tab, badge, label, children, class: cls, style, id }: ScreenProps) {
  const Tag = useContext(EmbeddedScreen) ? 'div' : 'main';
  return (
    <Tag
      class={cls} data-screen={id} aria-label={label}
      style={{
        ...themeVars(theme),
        position: 'relative', width: '100%', height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column',
        background: bg || 'var(--bg)', color: '#F5F1EA', fontFamily: 'var(--xm-font)',
        ['--xm-content-bottom' as string]: tab ? 'var(--xm-tabbar-h)' : '0px',
        animation: 'xmScreenIn 180ms ease-out both',
        ...style,
      }}
    >
      {children}
      {tab && <XTabs tab={tab} badge={badge} />}
    </Tag>
  );
}
